// Phase 2.10 (hh-1hz) — property/room tree loader for the manual-entry forms.
//
// The forms need a target property/room to file new/edited records under.
// The design's `PlacePicker` read a static `window.PROPERTIES`; here we fetch
// the live tree from GET /api/v1/properties/tree once on mount.

import { useEffect, useState } from 'preact/hooks';
import { properties, humanizeError } from '../../api';
import type { PropertyTreeNode } from '../../api/endpoints/properties.js';

export type TreeStatus = 'loading' | 'ready' | 'error';

export interface PropertyTreeState {
  readonly status: TreeStatus;
  readonly properties: readonly PropertyTreeNode[];
  readonly error: string | null;
  reload(): void;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

export function usePropertyTree(): PropertyTreeState {
  const [status, setStatus] = useState<TreeStatus>('loading');
  const [tree, setTree] = useState<readonly PropertyTreeNode[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    properties
      .getPropertyTree()
      .then((res) => {
        if (cancelled) return;
        setTree(res.properties);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return {
    status,
    properties: tree,
    error,
    reload: () => setNonce((n) => n + 1),
  };
}
